// frontend/src/utils/liveEta.js
// Shared helpers for canonical live ETA storage and client-side countdown aging.

const getFiniteEtaMinutes = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.max(0, numericValue) : null;
};

const getWaypointField = (waypoint, primaryField, fallbackField) => (
    waypoint?.[primaryField] ?? waypoint?.[fallbackField] ?? null
);
const getWaypointId = (waypoint) => Number(waypoint?.waypoint_id ?? waypoint?.id);

export const isNoDataEtaPayload = (etaPayload) => (
    ['no_data_available', 'incarnation_reset'].includes(etaPayload?.event)
    && etaPayload?.predictions === null
);

export const hasEtaEnvelope = (payload) => typeof payload?.incarnation === 'string'
    && payload.incarnation.length > 0 && Number.isSafeInteger(payload.publicationRevision)
    && payload.publicationRevision >= 0;
export const isEtaRetirement = payload => isNoDataEtaPayload(payload) || Boolean(payload?.eta_retirement);

export const getEtaTimestampMs = (etaPayload) => {
    if (!etaPayload?.timestamp) return null;
    const timestampMs = new Date(etaPayload.timestamp).getTime();
    return Number.isFinite(timestampMs) ? timestampMs : null;
};

/**
 * One ordering rule owns socket, REST, and local ETA ingress. The server's
 * publication revision orders payloads independently of timestamp or transport;
 * connection admission and retirement barriers are enforced by the singleton.
 */
export const shouldAcceptEtaUpdate = ({
    incomingEta,
    currentEta = null
}) => {
    if (!hasEtaEnvelope(incomingEta)) return false;
    if (!currentEta) return true;
    if (!hasEtaEnvelope(currentEta)) return true;
    if (incomingEta.incarnation !== currentEta.incarnation) return isEtaRetirement(incomingEta);
    // Publication order is authoritative even when two accepted calculations
    // share a timestamp or finish in a different order from their start times.
    return incomingEta.publicationRevision >= currentEta.publicationRevision;

};

const isDwellingWaypoint = (waypoint) => {
    if (!waypoint
        || waypoint.is_passed === true
        || waypoint.is_skipped === true
        || waypoint.is_manual_skip === true) {
        return false;
    }

    if (waypoint.point_type !== 'stop') {
        return false;
    }

    return Boolean(
        getWaypointField(waypoint, 'waypoint_arrival_time', 'arrival_time')
        && !getWaypointField(waypoint, 'waypoint_departure_time', 'departure_time')
    );
};

const hasDwellingWaypoint = (waypoints = []) => (
    Array.isArray(waypoints) && waypoints.some(isDwellingWaypoint)
);

// Client-side aging is an opt-in backend contract. Missing or ambiguous state
// must not age, because dwell/loading time is not active travel time and should
// not consume downstream ETA between WebSocket payloads.
const canAgeClientEta = (etaPayload) => (
    etaPayload?.active_eta_state === 'active_travel'
);

const normalizeStructuralEtaWaypoint = (waypoint = {}, originId = null) => {
    const isPassed = waypoint.is_passed === true;
    const isSkipped = waypoint.is_skipped === true || waypoint.is_manual_skip === true;
    const originalEtaMinutes = isSkipped ? null : getOriginalEtaMinutes(waypoint);

    if (isSkipped) {
        // Skipped waypoints intentionally keep null ETA semantics. Coercing
        // them to zero would make the UI read as "arrived" and would also break
        // the backend distinction between unavailable and passed.
        return {
            ...waypoint,
            original_eta_minutes_from_now: null,
            eta_minutes_from_now: null,
            estimated_arrival_time: null,
            dynamic_arrival_time: null,
            eta_minutes_with_operational_delay: null,
            boundary_time_with_operational_delay: null
        };
    }

    if (isPassed) {
        // Passed rows use the same selected boundary as future projections:
        // departure at the effective origin and arrival everywhere else. The
        // selected boundary fails closed instead of borrowing the other kind;
        // dynamic/estimated arrival aliases remain arrival fields and are not
        // overwritten with an origin departure timestamp.
        const waypointId = getWaypointId(waypoint);
        const recordedBoundaryTime = waypointId === originId
            ? (waypoint.waypoint_departure_time ?? waypoint.departure_time ?? null)
            : (waypoint.waypoint_arrival_time ?? waypoint.arrival_time ?? null);
        const boundaryTime = waypoint.boundary_time_with_operational_delay
            ?? recordedBoundaryTime;

        return {
            ...waypoint,
            original_eta_minutes_from_now: originalEtaMinutes,
            eta_minutes_from_now: 0,
            eta_minutes_with_operational_delay: 0,
            boundary_time_with_operational_delay: boundaryTime
        };
    }

    return {
        ...waypoint,
        original_eta_minutes_from_now: originalEtaMinutes
    };
};

export const getLiveEtaClientReceivedAtMs = (etaPayload) => {
    const timestampMs = Number(etaPayload?.client_received_at_ms);
    return Number.isFinite(timestampMs) ? timestampMs : null;
};

export const getOriginalEtaMinutes = (waypoint = {}) => {
    const preservedMinutes = getFiniteEtaMinutes(waypoint.original_eta_minutes_from_now);
    if (preservedMinutes !== null) {
        return preservedMinutes;
    }

    return getFiniteEtaMinutes(waypoint.eta_minutes_from_now);
};

export const createCanonicalLiveEtaPayload = (
    etaPayload,
    previousEtaPayload = null,
    receivedAtMs = Date.now()
) => {
    if (!etaPayload || !Array.isArray(etaPayload.waypoints)) {
        return etaPayload;
    }

    // Reusing the previous receive timestamp for identical backend payloads
    // prevents re-renders from resetting the countdown anchor and making ETA
    // appear to stop aging.
    const existingReceivedAtMs = getLiveEtaClientReceivedAtMs(etaPayload);
    const canReusePreviousReceivedAtMs = Boolean(
        etaPayload?.timestamp
        && previousEtaPayload?.timestamp
        && previousEtaPayload.timestamp === etaPayload.timestamp
    );
    const previousReceivedAtMs = canReusePreviousReceivedAtMs
        ? getLiveEtaClientReceivedAtMs(previousEtaPayload)
        : null;
    const clientReceivedAtMs = existingReceivedAtMs
        ?? previousReceivedAtMs
        ?? receivedAtMs;

    return {
        ...etaPayload,
        client_received_at_ms: clientReceivedAtMs,
        waypoints: etaPayload.waypoints.map((waypoint) => ({
            ...waypoint,
            original_eta_minutes_from_now: getOriginalEtaMinutes(waypoint)
        }))
    };
};

export const deriveLiveEtaPayload = (etaPayload, nowMs = Date.now()) => {
    if (!etaPayload || !Array.isArray(etaPayload.waypoints)) {
        return null;
    }

    // This helper expects a canonical live ETA payload from the acceptance/cache layer.
    // The fallback canonicalization below is defensive only, so a stray caller does not
    // crash the UI while still aging from a deterministic anchor.
    const canonicalEtaPayload = createCanonicalLiveEtaPayload(etaPayload, null, nowMs);
    // Resolve origin once from the incoming effective order before structural
    // normalization. Backend projection and client aging must select the same
    // first non-skipped waypoint, including when a leading row is manually
    // skipped but also carries legacy is_passed=true.
    const originWaypoint = canonicalEtaPayload.waypoints.find((waypoint) => {
        const waypointId = getWaypointId(waypoint);
        return !waypoint?.is_skipped
            && !waypoint?.is_manual_skip
            && Number.isInteger(waypointId)
            && waypointId > 0;
    });
    const originId = getWaypointId(originWaypoint);
    const normalizedEtaPayload = {
        ...canonicalEtaPayload,
        waypoints: canonicalEtaPayload.waypoints.map((waypoint) => (
            normalizeStructuralEtaWaypoint(waypoint, originId)
        ))
    };

    const shouldAgeEta = canAgeClientEta(normalizedEtaPayload)
        && !hasDwellingWaypoint(normalizedEtaPayload.waypoints);
    const clientReceivedAtMs = getLiveEtaClientReceivedAtMs(normalizedEtaPayload) ?? nowMs;
    const elapsedMs = shouldAgeEta ? Math.max(0, nowMs - clientReceivedAtMs) : 0;
    return {
        ...normalizedEtaPayload,
        waypoints: normalizedEtaPayload.waypoints.map((waypoint) => {
            const originalEtaMinutes = getOriginalEtaMinutes(waypoint);
            const isPassed = waypoint.is_passed === true;
            const isSkipped = waypoint.is_skipped === true || waypoint.is_manual_skip === true;
            let etaMinutesFromNow = null;
            let displayArrivalTime = null;

            if (isPassed || isSkipped) {
                return waypoint;
            }

            if (originalEtaMinutes === null) {
                // An unavailable ETA must not retain a stale display clock. The
                // duration remains authoritative, so no timestamp is invented
                // when the backend has not supplied a finite value.
                return {
                    ...waypoint,
                    original_eta_minutes_from_now: null,
                    eta_minutes_from_now: null,
                    estimated_arrival_time: null,
                    dynamic_arrival_time: null,
                    eta_minutes_with_operational_delay: null,
                    boundary_time_with_operational_delay: null
                };
            }

            etaMinutesFromNow = shouldAgeEta
                ? Math.max(0, originalEtaMinutes - (elapsedMs / 60000))
                : originalEtaMinutes;
            // Arrival aliases are display-only companions to the authoritative
            // ETA duration. Non-aging states use the same rounded minute value
            // as the live badge so a pinned duration cannot drift from its clock
            // between backend payloads; active travel keeps its raw stable target.
            const displayEtaMinutes = shouldAgeEta
                ? etaMinutesFromNow
                : Math.round(etaMinutesFromNow);
            displayArrivalTime = new Date(
                nowMs + (displayEtaMinutes * 60000)
            ).toISOString();
            const arrivalDelayMinutes = getFiniteEtaMinutes(
                waypoint.cumulative_arrival_delay_minutes
            ) ?? 0;
            const projectedEtaMinutes = getFiniteEtaMinutes(
                waypoint.eta_minutes_with_operational_delay
            );
            // Normal duration decay applies only to raw ETA. Operational Delay
            // remains a constant additive offset, but only the backend-selected
            // effective duration proves which boundary total applies. Cumulative
            // fields are presentation metadata, not client projection authority.
            const projectedDelayMinutes = projectedEtaMinutes === null
                ? null
                : Math.max(0, projectedEtaMinutes - originalEtaMinutes);
            const effectiveEtaMinutes = projectedDelayMinutes === null
                ? null
                : etaMinutesFromNow + projectedDelayMinutes;
            const boundaryTimeWithOperationalDelay = effectiveEtaMinutes === null
                ? null
                : new Date(nowMs + effectiveEtaMinutes * 60000).toISOString();

            return {
                ...waypoint,
                original_eta_minutes_from_now: originalEtaMinutes,
                eta_minutes_from_now: etaMinutesFromNow,
                estimated_arrival_time: displayArrivalTime,
                dynamic_arrival_time: displayArrivalTime,
                cumulative_arrival_delay_minutes: arrivalDelayMinutes,
                cumulative_departure_delay_minutes: getFiniteEtaMinutes(
                    waypoint.cumulative_departure_delay_minutes
                ) ?? arrivalDelayMinutes,
                eta_minutes_with_operational_delay: effectiveEtaMinutes,
                boundary_time_with_operational_delay: boundaryTimeWithOperationalDelay
            };
        })
    };
};
